import { trigger, state, style, transition, animate, AnimationTriggerMetadata } from '@angular/animations';

// Common timing constants to avoid duplication
const TIMING_EASE_OUT_400 = '400ms ease-out';
const TIMING_EASE_IN_300 = '300ms ease-in';
const TIMING_CUBIC_BEZIER_600 = '600ms cubic-bezier(0.4, 0.0, 0.2, 1)';
const TIMING_CUBIC_BEZIER_500 = '500ms cubic-bezier(0.4, 0.0, 0.2, 1)';

// Common style definitions
const HIDDEN_STYLE_TRANSLATE_Y_20 = style({ opacity: 0, transform: 'translateY(20px)' });
const VISIBLE_STYLE_TRANSLATE_Y_0 = style({ opacity: 1, transform: 'translateY(0)' });
const HIDDEN_STYLE_TRANSLATE_Y_60 = style({ opacity: 0, transform: 'translateY(60px)' });
const HIDDEN_STYLE_TRANSLATE_X_60 = style({ opacity: 0, transform: 'translateX(60px)' });
const VISIBLE_STYLE_TRANSLATE_X_0 = style({ opacity: 1, transform: 'translateX(0)' });
const HIDDEN_STYLE_SCALE_09 = style({ opacity: 0, transform: 'scale(0.9)' });
const VISIBLE_STYLE_SCALE_1 = style({ opacity: 1, transform: 'scale(1)' });

/**
 * Factory function to create state-based animations with common patterns
 * Reduces code duplication by generating animations with consistent structure
 */
function createStateBasedAnimation(
  name: string,
  hiddenStyle: ReturnType<typeof style>,
  visibleStyle: ReturnType<typeof style>,
  timing: string,
  enterStyle?: ReturnType<typeof style>,
  leaveConfig?: { timing: string; style: ReturnType<typeof style> }
): AnimationTriggerMetadata {
  const transitions: any[] = [
    state('hidden', hiddenStyle),
    state('visible', visibleStyle),
    transition('hidden => visible', [animate(timing)]),
    transition(':enter', [
      enterStyle || hiddenStyle,
      animate(timing, visibleStyle)
    ])
  ];

  if (leaveConfig) {
    transitions.push(
      transition(':leave', [animate(leaveConfig.timing, leaveConfig.style)])
    );
  }

  return trigger(name, transitions);
}

/**
 * Common page load animation - slides from bottom with fade
 * Used for smooth page transitions
 * Matches card-checkin component style: translateY(20px), 400ms ease-out
 */
export const pageLoadAnimation = createStateBasedAnimation(
  'pageLoad',
  HIDDEN_STYLE_TRANSLATE_Y_20,
  VISIBLE_STYLE_TRANSLATE_Y_0,
  TIMING_EASE_OUT_400
);

/**
 * Page load animation with larger slide from bottom (bottom to center effect)
 * Used for components that need more dramatic slide effect
 * translateY(60px) with 600ms cubic-bezier timing
 */
export const pageLoadAnimationBottomToCenter = createStateBasedAnimation(
  'pageLoad',
  HIDDEN_STYLE_TRANSLATE_Y_60,
  VISIBLE_STYLE_TRANSLATE_Y_0,
  TIMING_CUBIC_BEZIER_600,
  style({ opacity: 0, transform: 'translateY(100%)' }),
  {
    timing: TIMING_CUBIC_BEZIER_500,
    style: style({ opacity: 0, transform: 'translateY(-60px)' })
  }
);

/**
 * Fade in animation - simple opacity transition
 * Used for subtle content appearance
 */
export const fadeInAnimation = trigger('fadeIn', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate(TIMING_EASE_OUT_400, style({ opacity: 1 }))
  ]),
  transition(':leave', [
    animate(TIMING_EASE_IN_300, style({ opacity: 0 }))
  ])
]);

/**
 * Slide from right animation
 * Used for side panels or content sliding in from right
 */
export const slideFromRightAnimation = createStateBasedAnimation(
  'slideFromRight',
  HIDDEN_STYLE_TRANSLATE_X_60,
  VISIBLE_STYLE_TRANSLATE_X_0,
  TIMING_CUBIC_BEZIER_600,
  style({ opacity: 0, transform: 'translateX(100%)' }),
  {
    timing: TIMING_CUBIC_BEZIER_500,
    style: style({ opacity: 0, transform: 'translateX(-60px)' })
  }
);

/**
 * Scale and fade animation - centered scale with fade
 * Used for modal-like appearances
 */
export const scaleFadeAnimation = createStateBasedAnimation(
  'scaleFade',
  HIDDEN_STYLE_SCALE_09,
  VISIBLE_STYLE_SCALE_1,
  TIMING_EASE_OUT_400,
  undefined,
  {
    timing: TIMING_EASE_IN_300,
    style: style({ opacity: 0, transform: 'scale(0.95)' })
  }
);

/**
 * Slide from bottom animation - alias for pageLoadAnimation
 * Used for subtle content transitions
 * Note: This is identical to pageLoadAnimation, use pageLoadAnimation instead
 */
export const slideFromBottomAnimation = pageLoadAnimation;

